import React, { useEffect, useState, useRef } from 'react';

// Missile Barrage (316): number of small glowing yellow dots
const MISSILE_BARRAGE_DOT_COUNT = 8;

// Cubic Bezier: B(t) = (1-t)³P0 + 3(1-t)²t P1 + 3(1-t)t² P2 + t³ P3
const cubicBezier = (t, p0, p1, p2, p3) => {
  const u = 1 - t;
  const u2 = u * u, u3 = u2 * u;
  const t2 = t * t, t3 = t2 * t;
  return u3 * p0 + 3 * u2 * t * p1 + 3 * u * t2 * p2 + t3 * p3;
};

// Perpendicular offset from line (start->end) for curved "detour" paths
const perpendicularOffset = (start, end, distance) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const len = Math.hypot(dx, dy) || 1;
  const perpX = (-dy / len) * distance;
  const perpY = (dx / len) * distance;
  return { perpX, perpY };
};

/**
 * StrategemTargetAnimation - Ball projectile animation from strategem card in hand to target
 * Black ball for negative effects (damage), yellow ball for positive effects (healing)
 * Strategem 316 (Missile Barrage): many small glowing yellow dots in random chaotic trajectories
 * Strategem 321 (Precision Strike): single precision_missile.png image, nose always pointing at target
 * Strategem 317 (Guided Missile): single guided_missile.png image, nose always pointing at target
 * Strategem 116 (Reaction Shot): single dagger.png image, top always pointing at target (same math as Guided Missile)
 * Strategem 368 (Damage Pulse): single dmg_pulse.png image, top always pointing at target (same math as Guided Missile)
 * Strategem 54 (Repurposed Warhead): single repurposed_warhead.png image, nose always pointing at target
 * Strategem 228 (Whispers of Kek): kek_atom.png (same as Kek deploy targeting)
 * @param {Object} props
 * @param {Object} props.fromHand - { instanceId, element, position } - Source stratagem card
 * @param {Object} props.toSlot - { zone, index, playerId } - Target slot
 * @param {string} props.targetType - 'unit', 'zone', 'drone', or 'general'
 * @param {string} props.effectType - 'damage' (black) or 'heal' (yellow) - defaults to 'damage'
 * @param {number} props.strategemId - Strategem order ID (e.g. 316 for Missile Barrage)
 * @param {boolean} props.isOpponentStrategem - Whether this is an opponent/bot strategem (should start from opponent general)
 * @param {Function} props.onComplete - Callback when animation completes
 * @param {number} props.animationSpeedMultiplier - Speed multiplier (1 = normal, 0.5 = 2x slower)
 */
export const StrategemTargetAnimation = ({ fromHand, toSlot, targetType, effectType = 'damage', strategemId, isOpponentStrategem = false, onComplete, onDamageAtTarget, animationSpeedMultiplier = 1 }) => {
  const isMissileBarrage = strategemId === 316;
  const isPrecisionStrike = strategemId === 321;
  const isGuidedMissile = strategemId === 317;
  const isReactionShot = strategemId === 116;
  const isDamagePulse = strategemId === 368;
  const isRepurposedWarhead = strategemId === 54;
  const isWhispersOfKek = strategemId === 228; // Whispers of Kek: same projectile image as Kek (239)
  console.log('🎬🎬🎬 StrategemTargetAnimation RENDER', { 
    fromHand, 
    toSlot, 
    targetType,
    strategemId,
    isMissileBarrage,
    'fromHand.position': fromHand?.position 
  });
  
  // Initialize position from fromHand.position immediately so we render right away
  const initialPosition = fromHand?.position || { x: window.innerWidth / 2, y: window.innerHeight - 100 };
  console.log('🎬 Initial position:', initialPosition);
  const [position, setPosition] = useState(initialPosition);
  const [opacity, setOpacity] = useState(1);
  const [isVisible, setIsVisible] = useState(true);
  // Missile Barrage: positions of each dot [{ x, y }, ...]
  const [missileBarrageDots, setMissileBarrageDots] = useState([]);
  // Precision Strike (321): rotation in degrees so missile nose points at target (updated each frame)
  const [precisionMissileRotationDeg, setPrecisionMissileRotationDeg] = useState(0);
  // Guided Missile (317): rotation in degrees so missile nose points at target (updated each frame)
  const [guidedMissileRotationDeg, setGuidedMissileRotationDeg] = useState(0);
  // Reaction Shot (116): dagger.png, top points at target (same convention as Guided Missile)
  const [reactionShotDaggerRotationDeg, setReactionShotDaggerRotationDeg] = useState(0);
  // Damage Pulse (368): dmg_pulse.png, top points at target (same convention as Guided Missile)
  const [damagePulseRotationDeg, setDamagePulseRotationDeg] = useState(0);
  // Repurposed Warhead (54): rotation in degrees so warhead nose points at target (updated each frame)
  const [repurposedWarheadRotationDeg, setRepurposedWarheadRotationDeg] = useState(0);
  const animationRef = useRef(null);
  const timeoutRef = useRef(null);
  const startTimeRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const missileBarrageConfigRef = useRef(null); // { dots: [{ start, end, ctrl1, ctrl2, startDelay, duration }], toPixel }

  // Hardcoded slot coordinates (cell-based) - from DeployTargetAnimation
  // Grid format: "cell X,Y" where X is horizontal, Y is vertical
  // Grid uses 20px cell size (from GridOverlay.js)
  const slotCoordinates = {
    // Opponent Front (slots 1-5)
    'opponent_front_0': { cellX: 4, cellY: 10 },   // Slot 1
    'opponent_front_1': { cellX: 14, cellY: 10 }, // Slot 2
    'opponent_front_2': { cellX: 24, cellY: 9 },  // Slot 3
    'opponent_front_3': { cellX: 32, cellY: 10 }, // Slot 4
    'opponent_front_4': { cellX: 42, cellY: 9 },  // Slot 5
    
    // Opponent Flank (slots 6-10)
    'opponent_flank_0': { cellX: 52, cellY: 10 }, // Slot 6
    'opponent_flank_1': { cellX: 52, cellY: 11 }, // Slot 7
    'opponent_flank_2': { cellX: 70, cellY: 10 }, // Slot 8
    'opponent_flank_3': { cellX: 79, cellY: 10 }, // Slot 9
    'opponent_flank_4': { cellX: 86, cellY: 10 }, // Slot 10
    
    // Your Front (slots 11-15)
    'your_front_0': { cellX: 3, cellY: 18 },   // Slot 11
    'your_front_1': { cellX: 15, cellY: 18 }, // Slot 12
    'your_front_2': { cellX: 22, cellY: 18 }, // Slot 13
    'your_front_3': { cellX: 32, cellY: 17 }, // Slot 14
    'your_front_4': { cellX: 39, cellY: 18 }, // Slot 15
    
    // Your Flank (slots 16-20)
    'your_flank_0': { cellX: 52, cellY: 18 }, // Slot 16
    'your_flank_1': { cellX: 62, cellY: 18 }, // Slot 17
    'your_flank_2': { cellX: 69, cellY: 17 }, // Slot 18
    'your_flank_3': { cellX: 81, cellY: 18 }, // Slot 19
    'your_flank_4': { cellX: 89, cellY: 17 }, // Slot 20
    
    // Drone zone coordinates (approximate center of drone slots)
    'opponent_drone_0': { cellX: 10, cellY: 6 },
    'opponent_drone_1': { cellX: 25, cellY: 6 },
    'opponent_drone_2': { cellX: 40, cellY: 6 },
    'opponent_drone_3': { cellX: 55, cellY: 6 },
    'your_drone_0': { cellX: 10, cellY: 22 },
    'your_drone_1': { cellX: 25, cellY: 22 },
    'your_drone_2': { cellX: 40, cellY: 22 },
    'your_drone_3': { cellX: 55, cellY: 22 },
  };

  // Zone center coordinates for zone-targeting stratagems
  const zoneCenterCoordinates = {
    'opponent_front': { cellX: 24, cellY: 10 },
    'opponent_flank': { cellX: 70, cellY: 10 },
    'your_front': { cellX: 22, cellY: 18 },
    'your_flank': { cellX: 70, cellY: 18 },
    'front': { cellX: 22, cellY: 18 }, // Alias for your_front
    'flank': { cellX: 70, cellY: 18 }, // Alias for your_flank
  };

  // Convert cell coordinates to pixel coordinates
  const GRID_CELL_SIZE = 20;
  const GRID_OFFSET_X = 0;
  const GRID_OFFSET_Y = 0;

  const cellToPixel = (cellX, cellY) => {
    return {
      x: GRID_OFFSET_X + (cellX * GRID_CELL_SIZE) + (GRID_CELL_SIZE / 2),
      y: GRID_OFFSET_Y + (cellY * GRID_CELL_SIZE) + (GRID_CELL_SIZE / 2)
    };
  };

  const getTargetPixelPosition = (zone, index, playerId, targetTypeParam) => {
    // Handle general targets - use DOM lookup
    if (zone === 'general' || targetTypeParam === 'general') {
      let generalElement = null;
      
      // DOM lookup for general element
      
      if (playerId === 'your' || playerId === 'friendly') {
        generalElement = document.querySelector(`[data-zone="player_general"]`);
      } else if (playerId === 'opponent') {
        generalElement = document.querySelector(`[data-zone="opponent_general"]`);
      } else {
        // Fallback: try both if playerId is not recognized
        generalElement = document.querySelector(`[data-zone="player_general"]`) || 
                         document.querySelector(`[data-zone="opponent_general"]`);
      }
      
      if (generalElement) {
        const rect = generalElement.getBoundingClientRect();
        return {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
      }
      
      // Fallback positions
      if (playerId === 'your' || playerId === 'friendly') {
        return { x: 200, y: 600 };
      } else {
        return { x: 1600, y: 100 };
      }
    }

    // Handle zone targeting (entire zone, not specific unit)
    if (targetTypeParam === 'zone' && (index === null || index === undefined)) {
      // ✅ FIX: Normalize zone for zone targeting too
      let zoneKey = zone;
      if (zone === 'front' || zone === 'flank') {
        const playerPrefix = (playerId === 'opponent') ? 'opponent' : 'your';
        zoneKey = `${playerPrefix}_${zone}`;
      } else if (!zone.includes('_')) {
        const playerPrefix = (playerId === 'opponent') ? 'opponent' : 'your';
        zoneKey = `${playerPrefix}_${zone}`;
      }
      
      const coords = zoneCenterCoordinates[zoneKey];
      if (coords) {
        return cellToPixel(coords.cellX, coords.cellY);
      }
      console.warn(`⚠️ StrategemTargetAnimation: No zone center coordinates for ${zoneKey} (original: ${zone})`);
      return { x: 400, y: 300 }; // Fallback center
    }

    // Handle drone targeting
    if (targetTypeParam === 'drone' || zone === 'opponent_drone' || zone === 'your_drone') {
      const droneKey = `${zone}_${index}`;
      const coords = slotCoordinates[droneKey];
      if (coords) {
        return cellToPixel(coords.cellX, coords.cellY);
      }
      
      // Try to find drone element by data attribute
      const droneElement = document.querySelector(`[data-drone-index="${index}"][data-drone-owner="${playerId}"]`);
      if (droneElement) {
        const rect = droneElement.getBoundingClientRect();
        return {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
      }
      
      console.warn(`⚠️ StrategemTargetAnimation: No coordinates for drone ${droneKey}`);
      return { x: 200, y: 200 };
    }
    
    // Handle unit targeting (specific slot)
    // If zone is undefined/null, can't look up position
    if (!zone) {
      console.warn(`⚠️ StrategemTargetAnimation: zone is undefined, returning fallback position`);
      return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    }
    
    // ✅ FIX: Normalize zone to always include player prefix for consistent DOM lookup
    // This ensures we always use the correct zone name that matches the DOM attributes
    let normalizedZone = zone;
    if (zone === 'front' || zone === 'flank') {
      // Zone without prefix - add player prefix based on playerId
      const playerPrefix = (playerId === 'opponent') ? 'opponent' : 'your';
      normalizedZone = `${playerPrefix}_${zone}`;
    } else if (!zone.includes('_')) {
      // Zone without prefix and not 'front'/'flank' - still add prefix
      const playerPrefix = (playerId === 'opponent') ? 'opponent' : 'your';
      normalizedZone = `${playerPrefix}_${zone}`;
    }
    
    // Build the correct slot key using normalized zone and playerId
    let slotKey = `${normalizedZone}_${index}`;
    
    // Use DOM lookup for accurate positioning
    // ✅ ALWAYS use DOM lookup for accurate positioning
    // The hardcoded cell coordinates don't match actual screen positions
    console.log(`🎬 StrategemTargetAnimation: Looking for slot with zone="${normalizedZone}", index=${index}, original zone="${zone}", playerId="${playerId}"`);
    const slotElement = document.querySelector(`[data-slot-zone="${normalizedZone}"][data-slot-index="${index}"]`);
    
    if (slotElement) {
      const rect = slotElement.getBoundingClientRect();
      const pixelPos = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };
      console.log(`✅ StrategemTargetAnimation: Found slot element at (${pixelPos.x}, ${pixelPos.y})`);
      return pixelPos;
    }
    
    // Fallback to hardcoded coordinates if DOM lookup fails
    console.warn(`⚠️ StrategemTargetAnimation: DOM lookup failed for zone="${normalizedZone}", index=${index}, using hardcoded coords for slotKey="${slotKey}"`);
    const coords = slotCoordinates[slotKey];
    if (coords) {
      const pixelPos = cellToPixel(coords.cellX, coords.cellY);
      console.log(`⚠️ StrategemTargetAnimation: Using hardcoded coords at (${pixelPos.x}, ${pixelPos.y})`);
      return pixelPos;
    }
    
    // ✅ FIX: Better fallback - try to find any slot in the zone, or use zone center
    // This prevents animations from being skipped when repositioning moves units
    console.warn(`⚠️ StrategemTargetAnimation: No hardcoded coords for slotKey="${slotKey}", trying zone center fallback`);
    
    // Try to find any slot in the normalized zone (for repositioning cases where unit moved)
    const zoneSlots = document.querySelectorAll(`[data-slot-zone="${normalizedZone}"]`);
    if (zoneSlots.length > 0) {
      // Use the first slot in the zone as fallback
      const firstSlot = zoneSlots[0];
      const rect = firstSlot.getBoundingClientRect();
      const pixelPos = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };
      console.log(`⚠️ StrategemTargetAnimation: Using first slot in zone as fallback at (${pixelPos.x}, ${pixelPos.y})`);
      return pixelPos;
    }
    
    // Last resort: use zone center coordinates
    const zoneCenterKey = normalizedZone.replace('_0', '').replace('_1', '').replace('_2', '').replace('_3', '').replace('_4', '');
    const zoneCenterCoords = zoneCenterCoordinates[zoneCenterKey] || zoneCenterCoordinates[normalizedZone];
    if (zoneCenterCoords) {
      const pixelPos = cellToPixel(zoneCenterCoords.cellX, zoneCenterCoords.cellY);
      console.log(`⚠️ StrategemTargetAnimation: Using zone center as fallback at (${pixelPos.x}, ${pixelPos.y})`);
      return pixelPos;
    }
    
    // Final fallback: center of screen (better than {0, 0} which skips animation)
    console.error(`❌ StrategemTargetAnimation: All fallbacks failed for zone="${normalizedZone}", index=${index}, using screen center`);
    return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  };

  const getHandCardPosition = () => {
    // ✅ FIX: For opponent/bot strategems, always use the provided position or opponent general position
    // Don't try to find the card in DOM (it's in the opponent's hand, not visible)
    if (isOpponentStrategem) {
      // Use pre-calculated position if available (should be opponent general position)
      if (fromHand?.position) {
        console.log('🎬 StrategemTargetAnimation: Using provided position for opponent strategem:', fromHand.position);
        return fromHand.position;
      }
      
      // Fallback: Get opponent general position directly
      const opponentGeneralElement = document.querySelector('[data-zone="opponent_general"]');
      if (opponentGeneralElement) {
        const rect = opponentGeneralElement.getBoundingClientRect();
        const position = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
        console.log('🎬 StrategemTargetAnimation: Using opponent general position for bot strategem:', position);
        return position;
      }
      
      // Final fallback for opponent strategems
      console.warn('⚠️ StrategemTargetAnimation: Could not find opponent general, using fallback');
      return { x: window.innerWidth - 200, y: 100 };
    }
    
    // For player strategems: Use pre-calculated position if available (captured before card removal)
    if (fromHand?.position) {
      return fromHand.position;
    }
    
    // Try to find the stratagem card in hand by instanceId
    if (fromHand?.instanceId) {
      const cardElement = document.querySelector(`[data-instance-id="${fromHand.instanceId}"]`);
      if (cardElement) {
        const rect = cardElement.getBoundingClientRect();
        return {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
      }
    }
    
    // Fallback: approximate hand position (center-bottom of screen)
    console.warn('⚠️ StrategemTargetAnimation: Could not find hand card position, using fallback');
    return { x: window.innerWidth / 2, y: window.innerHeight - 100 };
  };

  useEffect(() => {
    console.log('🎬 StrategemTargetAnimation: Component mounted/updated', { fromHand, toSlot, targetType, isAnimating: isAnimatingRef.current });
    
    if (!fromHand || !toSlot || isAnimatingRef.current) {
      console.log('⚠️ StrategemTargetAnimation: Skipping - missing props or already animating', { fromHand, toSlot, isAnimating: isAnimatingRef.current });
      return;
    }

    console.log(`🎬 StrategemTargetAnimation: Starting animation`, { fromHand, toSlot, targetType });
    
    // Small delay to ensure DOM is ready (especially for hand card lookup)
    const startAnimation = () => {
      const fromPixel = getHandCardPosition();
      const toPixel = getTargetPixelPosition(toSlot.zone, toSlot.index, toSlot.playerId, targetType);
      
      // Validate positions - only skip if both are exactly 0 (which indicates a real failure)
      // Note: We now have better fallbacks, so this should rarely trigger
      if (toPixel.x === 0 && toPixel.y === 0) {
        console.error(`❌ StrategemTargetAnimation: Failed to get target position (both x and y are 0)`);
        if (onComplete) onComplete();
        return;
      }
          
      console.log(`🎬 StrategemTargetAnimation: Starting animation from hand to ${toSlot.zone}${toSlot.index !== null && toSlot.index !== undefined ? `[${toSlot.index}]` : ''}`);
      console.log(`🎬 Pixel coordinates: from (${fromPixel.x}, ${fromPixel.y}) to (${toPixel.x}, ${toPixel.y})`);
      
      isAnimatingRef.current = true;
      startTimeRef.current = Date.now();
      const speedFactor = animationSpeedMultiplier > 0 ? animationSpeedMultiplier : 1;
      const DURATION = Math.round(300 / speedFactor); // 300ms at 100% speed

      if (isMissileBarrage) {
        // Missile Barrage (316): fewer dots, each taking a curved detour (not straight line)
        const spread = 24;
        const dots = [];
        for (let i = 0; i < MISSILE_BARRAGE_DOT_COUNT; i++) {
          const start = {
            x: fromPixel.x + (Math.random() - 0.5) * spread * 2,
            y: fromPixel.y + (Math.random() - 0.5) * spread * 2
          };
          const end = {
            x: toPixel.x + (Math.random() - 0.5) * spread,
            y: toPixel.y + (Math.random() - 0.5) * spread
          };
          // Control points push path perpendicular to the line so dots arc away then back (less optimal route)
          const { perpX, perpY } = perpendicularOffset(start, end, 1);
          const detour = 70 + Math.random() * 90; // 70–160px perpendicular detour
          const side = Math.random() > 0.5 ? 1 : -1;
          const oneThirdX = start.x + (end.x - start.x) * 0.33;
          const oneThirdY = start.y + (end.y - start.y) * 0.33;
          const twoThirdX = start.x + (end.x - start.x) * 0.67;
          const twoThirdY = start.y + (end.y - start.y) * 0.67;
          const ctrl1 = {
            x: oneThirdX + perpX * detour * side,
            y: oneThirdY + perpY * detour * side
          };
          const ctrl2 = {
            x: twoThirdX + perpX * (detour * 0.8) * -side,
            y: twoThirdY + perpY * (detour * 0.8) * -side
          };
          dots.push({
            start,
            end,
            ctrl1,
            ctrl2,
            startDelay: Math.random() * 60,
            duration: DURATION * (0.85 + Math.random() * 0.5)
          });
        }
        missileBarrageConfigRef.current = { dots, toPixel };
        setMissileBarrageDots(dots.map(d => ({ x: d.start.x, y: d.start.y })));

        const animateMissileBarrage = () => {
          const elapsed = Date.now() - startTimeRef.current;
          const config = missileBarrageConfigRef.current;
          if (!config) return;
          const { dots: dotConfigs, toPixel: toPx } = config;
          const newPositions = dotConfigs.map((d, i) => {
            const t = Math.max(0, (elapsed - d.startDelay) / d.duration);
            const progress = Math.min(t, 1);
            const eased = progress < 0.5
              ? 2 * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 2) / 2;
            const x = cubicBezier(eased, d.start.x, d.ctrl1.x, d.ctrl2.x, d.end.x);
            const y = cubicBezier(eased, d.start.y, d.ctrl1.y, d.ctrl2.y, d.end.y);
            return { x, y };
          });
          setMissileBarrageDots(newPositions);
          const allDone = dotConfigs.every((d, i) => {
            const t = (elapsed - d.startDelay) / d.duration;
            return t >= 1;
          });
          if (allDone) {
            isAnimatingRef.current = false;
            animationRef.current = null;
            missileBarrageConfigRef.current = null;
            setIsVisible(false);
            if (onDamageAtTarget) {
              onDamageAtTarget({
                x: toPx.x,
                y: toPx.y,
                zone: toSlot.zone,
                index: toSlot.index,
                playerId: toSlot.playerId,
                targetType: targetType
              });
            }
            if (onComplete) onComplete();
          } else {
            animationRef.current = requestAnimationFrame(animateMissileBarrage);
          }
        };
        animationRef.current = requestAnimationFrame(animateMissileBarrage);
        return;
      }

      setPosition(fromPixel);
      setOpacity(1);
      if (isPrecisionStrike) {
        const dx = toPixel.x - fromPixel.x;
        const dy = toPixel.y - fromPixel.y;
        const angleRad = Math.atan2(dy, dx);
        setPrecisionMissileRotationDeg((angleRad * 180 / Math.PI) + 90);
      }
      if (isGuidedMissile) {
        const dx = toPixel.x - fromPixel.x;
        const dy = toPixel.y - fromPixel.y;
        const angleRad = Math.atan2(dy, dx);
        setGuidedMissileRotationDeg((angleRad * 180 / Math.PI) + 90);
      }
      if (isReactionShot) {
        const dx = toPixel.x - fromPixel.x;
        const dy = toPixel.y - fromPixel.y;
        const angleRad = Math.atan2(dy, dx);
        setReactionShotDaggerRotationDeg((angleRad * 180 / Math.PI) + 90);
      }
      if (isDamagePulse) {
        const dx = toPixel.x - fromPixel.x;
        const dy = toPixel.y - fromPixel.y;
        const angleRad = Math.atan2(dy, dx);
        setDamagePulseRotationDeg((angleRad * 180 / Math.PI) + 90);
      }
      if (isRepurposedWarhead) {
        const dx = toPixel.x - fromPixel.x;
        const dy = toPixel.y - fromPixel.y;
        const angleRad = Math.atan2(dy, dx);
        setRepurposedWarheadRotationDeg((angleRad * 180 / Math.PI) + 90);
      }
      
      const animate = () => {
        const elapsed = Date.now() - startTimeRef.current;
        const progress = Math.min(elapsed / DURATION, 1);
        
        // Ease-in-out easing
        const eased = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        
        // Interpolate position
        const currentX = fromPixel.x + (toPixel.x - fromPixel.x) * eased;
        const currentY = fromPixel.y + (toPixel.y - fromPixel.y) * eased;
        
        setPosition({ x: currentX, y: currentY });
        // Precision Strike / Guided Missile: keep missile nose pointing at target (image top = -90deg in CSS)
        if (isPrecisionStrike) {
          const dx = toPixel.x - currentX;
          const dy = toPixel.y - currentY;
          const angleRad = Math.atan2(dy, dx);
          setPrecisionMissileRotationDeg((angleRad * 180 / Math.PI) + 90);
        }
        if (isGuidedMissile) {
          const dx = toPixel.x - currentX;
          const dy = toPixel.y - currentY;
          const angleRad = Math.atan2(dy, dx);
          setGuidedMissileRotationDeg((angleRad * 180 / Math.PI) + 90);
        }
        if (isReactionShot) {
          const dx = toPixel.x - currentX;
          const dy = toPixel.y - currentY;
          const angleRad = Math.atan2(dy, dx);
          setReactionShotDaggerRotationDeg((angleRad * 180 / Math.PI) + 90);
        }
        if (isDamagePulse) {
          const dx = toPixel.x - currentX;
          const dy = toPixel.y - currentY;
          const angleRad = Math.atan2(dy, dx);
          setDamagePulseRotationDeg((angleRad * 180 / Math.PI) + 90);
        }
        if (isRepurposedWarhead) {
          const dx = toPixel.x - currentX;
          const dy = toPixel.y - currentY;
          const angleRad = Math.atan2(dy, dx);
          setRepurposedWarheadRotationDeg((angleRad * 180 / Math.PI) + 90);
        }
        
        if (progress < 1) {
          const rafId = requestAnimationFrame(animate);
          animationRef.current = rafId;
        } else {
          // Animation complete - trigger damage animation at target position
          console.log(`✅ StrategemTargetAnimation: Animation complete at (${toPixel.x}, ${toPixel.y})`);
          isAnimatingRef.current = false;
          animationRef.current = null;
          setIsVisible(false);
          
          // Trigger damage animation at the exact target position
          if (onDamageAtTarget) {
            onDamageAtTarget({
              x: toPixel.x,
              y: toPixel.y,
              zone: toSlot.zone,
              index: toSlot.index,
              playerId: toSlot.playerId,
              targetType: targetType
            });
          }
          
          if (onComplete) onComplete();
        }
      };
      
      const rafId = requestAnimationFrame(animate);
      animationRef.current = rafId;
    };
    
    // Small delay to ensure DOM is ready
    timeoutRef.current = setTimeout(() => {
      startAnimation();
    }, 50);
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      isAnimatingRef.current = false;
      // Critical: call onComplete so completeAnimation cleans up ghost units in ghostUnitsRef.
      // Without this, ghost units persist when component unmounts or effect re-runs mid-animation.
      if (onComplete) onComplete();
    };
  }, [fromHand?.instanceId, toSlot?.zone, toSlot?.index, toSlot?.playerId, targetType, isMissileBarrage, isPrecisionStrike, isGuidedMissile, isReactionShot, isDamagePulse, isRepurposedWarhead]);

  // Ball style - black for damage, yellow for healing (single projectile)
  const getProjectileStyle = () => {
    const isHealing = effectType === 'heal';
    return {
      position: 'fixed',
      left: `${position.x}px`,
      top: `${position.y}px`,
      transform: 'translate(-50%, -50%)',
      width: '30px',
      height: '30px',
      borderRadius: '50%',
      pointerEvents: 'none',
      zIndex: 10000,
      opacity: opacity,
      transition: 'none',
      backgroundColor: isHealing ? '#ffd700' : 'black', // Yellow for healing, black for damage
      boxShadow: isHealing 
        ? '0 0 10px rgba(255, 215, 0, 0.8)' 
        : '0 0 10px rgba(0, 0, 0, 0.8)',
      border: isHealing 
        ? '2px solid rgba(255, 215, 0, 0.5)' 
        : '2px solid rgba(0, 0, 0, 0.5)'
    };
  };

  // Always render if we have valid initial position
  if (!isVisible) {
    return null;
  }

  // Missile Barrage (316): many small glowing yellow dots
  if (isMissileBarrage && missileBarrageDots.length > 0) {
    return (
      <>
        {missileBarrageDots.map((dot, i) => (
          <div
            key={i}
            style={{
              position: 'fixed',
              left: `${dot.x}px`,
              top: `${dot.y}px`,
              transform: 'translate(-50%, -50%)',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              pointerEvents: 'none',
              zIndex: 10000,
              backgroundColor: '#ffd700',
              boxShadow: '0 0 8px rgba(255, 215, 0, 0.95), 0 0 14px rgba(255, 220, 0, 0.6)',
              border: '1px solid rgba(255, 255, 200, 0.9)'
            }}
          />
        ))}
      </>
    );
  }

  // Precision Strike (321): missile image with nose always pointing at target
  if (isPrecisionStrike) {
    return (
      <img
        src="/images/precision_missile.png"
        alt=""
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${precisionMissileRotationDeg}deg)`,
          width: '53px',
          height: 'auto',
          maxHeight: '132px',
          pointerEvents: 'none',
          zIndex: 10000,
          opacity,
          transition: 'none'
        }}
      />
    );
  }

  // Guided Missile (317): missile image with nose always pointing at target
  if (isGuidedMissile) {
    return (
      <img
        src="/images/guided_missile.png"
        alt=""
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${guidedMissileRotationDeg}deg)`,
          width: '53px',
          height: 'auto',
          maxHeight: '132px',
          pointerEvents: 'none',
          zIndex: 10000,
          opacity,
          transition: 'none'
        }}
      />
    );
  }

  // Reaction Shot (116): dagger.png, top always pointing at target (same motion/rotation as Guided Missile)
  if (isReactionShot) {
    return (
      <img
        src="/images/dagger.png"
        alt=""
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${reactionShotDaggerRotationDeg}deg)`,
          width: '53px',
          height: 'auto',
          maxHeight: '132px',
          pointerEvents: 'none',
          zIndex: 10000,
          opacity,
          transition: 'none'
        }}
      />
    );
  }

  // Damage Pulse (368): dmg_pulse.png, top always pointing at target (same motion/rotation as Guided Missile)
  if (isDamagePulse) {
    return (
      <img
        src="/images/dmg_pulse.png"
        alt=""
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${damagePulseRotationDeg}deg)`,
          width: '53px',
          height: 'auto',
          maxHeight: '132px',
          pointerEvents: 'none',
          zIndex: 10000,
          opacity,
          transition: 'none'
        }}
      />
    );
  }

  // Repurposed Warhead (54): warhead image with nose always pointing at target (larger than other missiles)
  if (isRepurposedWarhead) {
    return (
      <img
        src="/images/repurposed_warhead.png"
        alt=""
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${repurposedWarheadRotationDeg}deg)`,
          width: '120px',
          height: 'auto',
          maxHeight: '297px',
          pointerEvents: 'none',
          zIndex: 10000,
          opacity,
          transition: 'none'
        }}
      />
    );
  }

  // Whispers of Kek (228): same kek_atom image as Kek (239) deploy targeting
  if (isWhispersOfKek) {
    return (
      <div
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: 'translate(-50%, -50%)',
          width: '40px',
          height: '40px',
          pointerEvents: 'none',
          zIndex: 10000,
          opacity,
          transition: 'none'
        }}
      >
        <img
          src="/images/kek_atom.png"
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </div>
    );
  }

  return (
    <div style={getProjectileStyle()} />
  );
};

export default StrategemTargetAnimation;
